import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, Card, CircularProgress, Grid, IconButton, Stack, Typography } from "@mui/material";
import { Methods } from "api";
import { useAPI, usePopups } from "hooks";
import { type ChangeEvent, type Dispatch, type SetStateAction, useEffect, useState } from "react";
import type { CcgUpdateClaimInput, Claim, ClaimDocument } from "s12-library/types";
import { uploadToPresignedUrl } from "utils/fileUpload/fileUpload";
import { compressImage } from "utils/fileUpload/imageUtils";

const MAX_SELECTION_MB = 4;
const MAX_UPLOAD_MB = 0.5;
const MAX_FILE_UPLOADS_PER_CLAIM = 5;

const ClaimDocumentUpload = ({
  claimId,
  claimDetails,
  isReceiptsConfirmed,
  setIsReceiptsConfirmed,
  setData,
  updateClaim,
  userPermissions,
}: {
  claimId: string;
  claimDetails: Claim;
  isReceiptsConfirmed?: boolean;
  setIsReceiptsConfirmed?: Dispatch<SetStateAction<boolean>>;
  setData?: Dispatch<SetStateAction<Claim | undefined>>;
  updateClaim?: (args?: { input: CcgUpdateClaimInput }) => Promise<Claim[] | undefined>;
  userPermissions?: {
    claimApprover: boolean;
    claimPayer: boolean;
  };
}) => {
  const { handleBannerMessage } = usePopups();

  const [initialComponentLoad, setInitialComponentLoad] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState<Partial<ClaimDocument>[]>([]);
  const [isSelectingDocument, setIsSelectingDocument] = useState<boolean>(false);
  const [isUploadingDocument, setIsUploadingDocument] = useState<number | null>(null);
  const [isDeleteDocument, setIsDeleteDocument] = useState<number | null>(null);
  const [userMessage, setUserMessage] = useState<string | null>(null);

  // Component state handlers
  useEffect(() => {
    if (claimId === claimDetails.id) {
      // Start fresh for new claim
      setInitialComponentLoad(true);
      setUploadedFiles([]);

      // Load new claim's documents
      if (claimDetails?.claimDocuments?.length) {
        setUploadedFiles(claimDetails.claimDocuments);
      }

      // Done loading
      setInitialComponentLoad(false);
    } else {
      setInitialComponentLoad(true);
      setUploadedFiles([]);
    }
  }, [claimId, claimDetails, setIsReceiptsConfirmed]);

  // API calls
  const { trigger: getPresignedUrlTrigger } = useAPI<any, any>({
    fieldName: "getPresignedUrl",
    method: Methods.GET,
    manual: true,
  });

  const { trigger: deleteDeleteDocumentTrigger } = useAPI<any, any>({
    fieldName: "deleteDocument",
    method: Methods.DELETE,
    manual: true,
  });

  const { trigger: createUpdateClaimDocumentTrigger } = useAPI<any, any>({
    fieldName: "createUpdateClaimDocument",
    method: Methods.PUT,
    manual: true,
  });

  const { trigger: deleteClaimDocumentTrigger } = useAPI<any, any>({
    fieldName: "deleteClaimDocument",
    method: Methods.PUT,
    manual: true,
  });

  // Actions
  const handleSelectDocument = async (event: ChangeEvent<HTMLInputElement>) => {
    try {
      setIsSelectingDocument(true);

      const files = event.target.files;
      if (!files || files.length === 0) return;

      const fileArray = Array.from(files);

      const remainingSlots = MAX_FILE_UPLOADS_PER_CLAIM - uploadedFiles.length;

      if (remainingSlots <= 0) {
        handleBannerMessage("error", "Limit Exceeded, Maximum of 5 documents allowed per claim.");
        return;
      }

      if (fileArray.length > remainingSlots) {
        handleBannerMessage(
          "info",
          `Only ${remainingSlots} more file(s) can be uploaded. ${
            fileArray.length - remainingSlots
          } file(s) were skipped.`,
        );
      }

      const filesToUpload = fileArray.slice(0, remainingSlots);

      for (let i = 0; i < filesToUpload.length; i++) {
        await handleUploadDocument(i, filesToUpload[i]);
      }

      event.target.value = "";
    } catch (error) {
      console.error("Error selecting document:", error);
    } finally {
      setIsSelectingDocument(false);
    }
  };

  const handleUploadDocument = async (key: number, file: File) => {
    try {
      if (uploadedFiles.length + 1 > MAX_FILE_UPLOADS_PER_CLAIM) {
        const msg = "Maximum of 5 documents allowed per claim.";
        setUserMessage(msg);
        handleBannerMessage("error", `Limit Exceeded, ${msg}`);
        return;
      }

      try {
        setIsUploadingDocument(key);

        let postBlob: Blob = file;

        const originalSizeMB = file.size / (1024 * 1024);

        if (originalSizeMB > MAX_SELECTION_MB) {
          const msg = `File "${file.name}" is too large (${originalSizeMB.toFixed(2)} MB).`;
          setUserMessage(msg);
          handleBannerMessage("error", msg);
          return;
        }

        if (originalSizeMB > MAX_UPLOAD_MB) {
          const { blob: compressedBlob } = await compressImage(file, MAX_UPLOAD_MB);
          postBlob = compressedBlob;
        }

        const compressedSizeMB = postBlob.size / (1024 * 1024);

        if (compressedSizeMB > MAX_UPLOAD_MB) {
          const msg = `${file.name} is still too large after compression: ${compressedSizeMB.toFixed(2)} MB`;
          setUserMessage(msg);
          handleBannerMessage("error", `Compression Failed, ${msg}`);
          return;
        }

        const response = await getPresignedUrlTrigger({
          fileName: file.name,
          fileType: file.type,
          referenceId: claimDetails.id,
        });

        const { uploadUrl, fileUrl } = response?.body ?? {};

        if (!uploadUrl || !fileUrl) {
          handleBannerMessage("error", `Upload Failed, Missing upload URL for ${file.name}`);
          return;
        }

        const success = await uploadToPresignedUrl(uploadUrl, postBlob, file.type);

        if (!success) return;

        const newFile = {
          url: fileUrl,
          type: file.type,
          documentType: "image",
        };

        setUploadedFiles((prev) => {
          const updatedFiles = [...prev, newFile];

          createUpdateClaimDocumentTrigger({
            id: claimDetails.id,
            claimDocuments: updatedFiles,
          });

          setData?.({
            ...claimDetails,
            claimDocuments: updatedFiles as ClaimDocument[],
          });

          return updatedFiles;
        });
      } catch (err) {
        console.error(err);
        handleBannerMessage("error", `Upload failed for ${file.name}`);
      } finally {
        setIsUploadingDocument(null);
      }
    } catch (error) {
      console.error("Error uploading document:", error);
    }
  };

  const handleDeleteDocument = async (key: number, file: Partial<ClaimDocument>) => {
    try {
      setIsDeleteDocument(key);
      const response = await deleteDeleteDocumentTrigger({ url: file.url });

      if (response?.statusCode === 200 && response?.body?.url) {
        const deletedUrl = response.body.url;
        const updatedFiles = uploadedFiles.filter((uploaded) => uploaded.url !== deletedUrl);

        await deleteClaimDocumentTrigger({
          id: claimDetails.id,
          claimDocuments: updatedFiles,
        });

        setUploadedFiles(updatedFiles);

        // Update parent claim data
        setData?.({
          ...claimDetails,
          claimDocuments: updatedFiles as ClaimDocument[],
        });

        setIsDeleteDocument(null);
      } else {
        handleBannerMessage("error", `Delete Failed, ${response?.body?.message || "Unexpected error occurred"}`);
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      handleBannerMessage("error", "Error, An error occurred while deleting the document.");
    } finally {
      setIsDeleteDocument(null);
    }
  };

  const isReadOnlyStatus = ["rejected", "approved_and_paid", "approved", "duplicated"].includes(claimDetails.status);
  const onApproveStage = !isReadOnlyStatus;

  if (initialComponentLoad) return <CircularProgress />;

  return (
    <Grid
      m={1}
      p={2}
      component={Card}
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        borderRadius: 1,
      }}>
      <Grid container spacing={3} sx={{ height: "fit-content" }}>
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Typography variant="h1">Receipts</Typography>
            {onApproveStage && uploadedFiles.length > 0 && (
              <Button
                component="label"
                variant="contained"
                disabled={isSelectingDocument || isUploadingDocument !== null}>
                {isSelectingDocument ? <CircularProgress size={24} /> : "Upload Receipts"}
                <input type="file" hidden multiple accept="image/*" onChange={handleSelectDocument} />
              </Button>
            )}
          </Stack>
        </Grid>
        <Grid container spacing={2} mt={1} ml={1}>
          <Grid item xs={12} key={"Receipts"}>
            <Box>
              {userMessage && (
                <Typography color="error" mb={2}>
                  {userMessage}
                </Typography>
              )}

              <Grid container spacing={2}>
                {uploadedFiles.length > 0 ? (
                  <>
                    {uploadedFiles.map((file, idx) => (
                      <Grid item xs={4} sm={3} md={2} key={file.url} position="relative">
                        <Box position="relative">
                          <a href={file.url} target="_blank" rel="noopener noreferrer">
                            <img
                              src={file.url}
                              alt={`Selected ${idx + 1}`}
                              style={{
                                width: "100%",
                                height: 150,
                                objectFit: "cover",
                                borderRadius: 8,
                              }}
                            />
                          </a>
                          {!isReadOnlyStatus && (
                            <IconButton
                              onClick={() => handleDeleteDocument(idx, file)}
                              disabled={isDeleteDocument !== null || isDeleteDocument === idx}
                              sx={{
                                position: "absolute",
                                top: 6,
                                right: 6,
                                bgcolor: "rgba(255,255,255,0.85)",
                                boxShadow: 1,
                                backdropFilter: "blur(2px)",
                              }}
                              size="small">
                              {isDeleteDocument === idx ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DeleteIcon color="error" fontSize="small" />
                              )}
                            </IconButton>
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </>
                ) : (
                  <>
                    {isReadOnlyStatus && (
                      <Grid item xs={12}>
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          width="100%"
                          color="text.secondary">
                          No uploaded files to display.
                        </Box>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>

              {!isReadOnlyStatus && updateClaim && userPermissions?.claimApprover && uploadedFiles.length > 0 && (
                <Box mt={2}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <input
                      type="checkbox"
                      id="confirm-receipt"
                      checked={isReceiptsConfirmed}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setIsReceiptsConfirmed?.(checked);
                        updateClaim({
                          input: {
                            id: [claimDetails.id],
                            isReceiptsConfirmed: checked,
                            status: claimDetails.status as any,
                          },
                        })
                          .then(() => {
                            setData?.({
                              ...claimDetails,
                              isReceiptsConfirmed: checked,
                            });
                          })
                          .catch(() => {
                            handleBannerMessage("error", "Failed to save receipt confirmation status.");
                          });
                      }}
                    />
                    <label htmlFor="confirm-receipt">
                      <Typography variant="body2">I confirm the receipt(s) is/are valid.</Typography>
                    </label>
                  </Stack>
                </Box>
              )}

              {!isReadOnlyStatus && uploadedFiles.length === 0 && (
                <Box
                  height={150}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ border: "2px dashed #ccc", borderRadius: 2, backgroundColor: "#fafafa" }}>
                  <Button component="label" variant="outlined" disabled={isSelectingDocument}>
                    {isSelectingDocument ? <CircularProgress size={20} /> : "Upload Receipts"}
                    <input type="file" hidden multiple accept="image/*" onChange={handleSelectDocument} />
                  </Button>
                </Box>
              )}

              <Box mt={2}>
                <Typography variant="caption" color="error">
                  * Maximum upload file size: 4 MB
                </Typography>
                <br />
                <Typography variant="caption" color="error">
                  * Maximum number of files: 5
                </Typography>
                <br />
                <Typography variant="caption" color="error">
                  * Only image files are supported
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ClaimDocumentUpload;
